import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setResume, setResumeFormState } from '../../redux/Slices/ResumeSlice';
import {
  useCreateResumeMutation,
  useFetchDropdownInfoQuery,
  useUpdateResumeMutation
} from '../../services/resumeBuilderApi';

import AchievementsForm from './AchievementsForm';
import AdditionalInfoForm from './AdditionalInfoForm';
import CertificationsForm from './CertificationsForm';
import ContactInfoForm from './ContactInfoForm';
import EducationForm from './EducationForm';
import ExperienceForm from './ExperienceForm';
import LanguagesForm from './LanguagesForm';
import PersonalInfoForm from './PersonalInfoForm';
import PreferencesForm from './PreferencesForm';
import ProjectsForm from './ProjectsForm';
import SkillsForm from './SkillsForm';

type Props = {
  onNext: () => void;
  onBack: () => void;
};

const stepNames = [
  'Personal Info',
  'Contact Info',
  'Skills',
  'Experience',
  'Education',
  'Projects',
  'Certifications',
  'Achievements',
  'Languages',
  'Preferences',
  'Additional Info'
];

const ResumeDetailsPage: React.FC<Props> = ({
  onNext,
  onBack
}: {
  onNext: any;
  onBack: any;
}) => {
  const dispatch = useDispatch();
  const [softSaveResume] = useUpdateResumeMutation();
  const [createResume] = useCreateResumeMutation();
  const { data: dropdownData, isLoading } =
    useFetchDropdownInfoQuery(undefined);

  const dropdowns = dropdownData?.data;
  const resume = useSelector((state: any) => state.root.resume);

  const totalSteps = 11;

  const getInitialStep = () => {
    const saved = resume.resumeFormState?.currentStep;
    if (typeof saved === 'number' && saved >= 1 && saved <= totalSteps)
      return saved;
    return 1;
  };

  const [step, setStep] = useState(getInitialStep);

  const nextStep = () => {
    if (step < totalSteps) {
      const next = step + 1;
      setStep(next);
      dispatch(setResumeFormState({ currentStep: next }));
    } else {
      onNext();
    }
  };

  const prevStep = () => {
    if (step > 1) {
      const prev = step - 1;
      setStep(prev);
      dispatch(setResumeFormState({ currentStep: prev }));
    } else {
      onBack();
    }
  };

  const saveInProgressRef = useRef(false);

  const triggerSoftSave = async () => {
    const payload = structuredClone(resume);
    if (saveInProgressRef.current) return;
    if (!resume.id) {
      const resumeId = await createResume(payload).unwrap();
      payload.id = resumeId?.data; // Adjust based on actual response structure
      dispatch(setResume(payload));
    }
    payload.resumeFormState = { currentStep: step };

    try {
      saveInProgressRef.current = true;
      await softSaveResume(payload).unwrap();
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      saveInProgressRef.current = false;
    }
  };

  useEffect(() => {
    const apiCall = setTimeout(async () => {
      await triggerSoftSave();
    }, 3000);

    return () => clearTimeout(apiCall);
  }, [
    step,
    resume.personalInfo,
    resume.skills,
    resume.experience,
    resume.education,
    resume.projects,
    resume.certifications,
    resume.achievements,
    resume.languages,
    resume.preferences,
    resume.additionalInfo
  ]);

  const renderStep = () => {
    const commonProps = {
      step,
      totalSteps,
      stepName: stepNames[step - 1],
      prevStepName: step > 1 ? stepNames[step - 2] : '',
      onNext: nextStep,
      onBack: prevStep,
      dropdowns
    };

    switch (step) {
      case 1:
        return <PersonalInfoForm {...commonProps} />;

      case 2:
        return <ContactInfoForm {...commonProps} />;

      case 3:
        return <SkillsForm {...commonProps} />;

      case 4:
        return <ExperienceForm {...commonProps} />;

      case 5:
        return <EducationForm {...commonProps} />;

      case 6:
        return <ProjectsForm {...commonProps} />;

      case 7:
        return <CertificationsForm {...commonProps} />;

      case 8:
        return <AchievementsForm {...commonProps} />;

      case 9:
        return <LanguagesForm {...commonProps} />;

      case 10:
        return <PreferencesForm {...commonProps} />;

      case 11:
        return <AdditionalInfoForm {...commonProps} />;

      default:
        return <PersonalInfoForm {...commonProps} />;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className='mb-6 text-center lg:text-left'>
        <p className='text-[11px] font-semibold uppercase tracking-wider text-primary'>
          Step 02
        </p>
        <h1 className='mb-2 text-xl font-extrabold lg:text-2xl'>
          Fill the details for building new resume
        </h1>
        <p className='text-sm text-gray-500'>
          Precision is key. Let&apos;s capture your journey.
        </p>
      </div>

      <div>
        {/* form content */}
        {renderStep()}
      </div>
    </div>
  );
};

export default ResumeDetailsPage;
